// skipcq: JS-W1028
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import useBeerReviewsForBeer from '../../hooks/useBeerReviewsForBeer';
import useBeerReviewTags from '../../hooks/useBeerReviewTags';
import { BrewerySessionContext } from '../../context/BrewerySessionContext';
import {
  trackPintPortfolioFilterBeerReviewsRating,
  trackPintPortfolioFilterBeerReviewsConsumptionPlace,
  trackPintPortfolioViewBeerReviews,
} from '../../utils/brewhousePortalAnalytics';
import BeerReviewCard from '../reviews/BeerReviewCard';
import {
  BEER_REVIEW_RATING_FILTER_OPTIONS,
  filterBeerReviewsByRating,
} from '../../utils/brewhousePortfolio/filterBeerReviewsByRating';
import {
  BEER_REVIEW_CONSUMPTION_PLACE_FILTER_OPTIONS,
  filterBeerReviewsByConsumptionPlace,
} from '../../utils/brewhousePortfolio/filterBeerReviewsByConsumptionPlace';

function PintPortfolioBeerReviewsPanel({ breweryId, beerId, enabled }) {
  const { breweryId: activeBreweryId = null } = useContext(BrewerySessionContext);
  const [ratingFilter, setRatingFilter] = useState('mostRecent');
  const [consumptionPlaceFilter, setConsumptionPlaceFilter] = useState('all');
  const { reviews, loading, error } = useBeerReviewsForBeer({ beerId, enabled });
  const { tagLabelMap } = useBeerReviewTags();
  const trackedLoadRef = useRef(false);

  const filteredReviews = useMemo(() => {
    const byRating = filterBeerReviewsByRating(reviews, ratingFilter);
    return filterBeerReviewsByConsumptionPlace(
      byRating,
      consumptionPlaceFilter,
      activeBreweryId ?? '',
    );
  }, [reviews, ratingFilter, consumptionPlaceFilter, activeBreweryId]);

  useEffect(() => {
    if (!enabled) {
      trackedLoadRef.current = false;
    }
  }, [enabled, beerId]);

  useEffect(() => {
    if (!enabled || loading || trackedLoadRef.current || !breweryId || !beerId) {
      return;
    }

    trackedLoadRef.current = true;
    try {
      trackPintPortfolioViewBeerReviews({
        breweryId,
        beerId,
        reviewCount: reviews.length,
        hasError: Boolean(error),
      });
    } catch {
      // Review list must render even when analytics capture fails.
    }
  }, [enabled, loading, error, reviews.length, breweryId, beerId]);

  const handleFilterChange = useCallback(
    (event) => {
      const nextFilter = event.target.value;
      setRatingFilter(nextFilter);
      if (breweryId && beerId) {
        try {
          trackPintPortfolioFilterBeerReviewsRating({
            breweryId,
            beerId,
            filterValue: nextFilter,
          });
        } catch {
          // Filter must apply even when analytics capture fails.
        }
      }
    },
    [breweryId, beerId],
  );

  const handleConsumptionPlaceFilterChange = useCallback(
    (event) => {
      const nextFilter = event.target.value;
      setConsumptionPlaceFilter(nextFilter);
      if (breweryId && beerId) {
        try {
          trackPintPortfolioFilterBeerReviewsConsumptionPlace({
            breweryId,
            beerId,
            filterValue: nextFilter,
          });
        } catch {
          // Filter must apply even when analytics capture fails.
        }
      }
    },
    [breweryId, beerId],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Unable to load reviews. Please try again later.
      </Alert>
    );
  }

  if (reviews.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No reviews yet for this beer.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel id="pint-portfolio-review-filter-label">Rating</InputLabel>
          <Select
            labelId="pint-portfolio-review-filter-label"
            id="pint-portfolio-review-filter"
            value={ratingFilter}
            label="Rating"
            onChange={handleFilterChange}
          >
            {BEER_REVIEW_RATING_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel id="pint-portfolio-consumption-filter-label">Consumption</InputLabel>
          <Select
            labelId="pint-portfolio-consumption-filter-label"
            id="pint-portfolio-consumption-filter"
            value={consumptionPlaceFilter}
            label="Consumption"
            onChange={handleConsumptionPlaceFilterChange}
          >
            {BEER_REVIEW_CONSUMPTION_PLACE_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredReviews.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reviews match the selected filters.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            wordBreak: 'break-word',
            width: '100%',
          }}
        >
          {filteredReviews.map((review) => (
            <BeerReviewCard
              key={review.id}
              review={review}
              variant="profile"
              tagLabelMap={tagLabelMap}
              breweryId={breweryId}
              beerId={beerId}
              showBreweryReactionDock
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

PintPortfolioBeerReviewsPanel.propTypes = {
  breweryId: PropTypes.string,
  beerId: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
};

export default PintPortfolioBeerReviewsPanel;
